import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../../configuration";
import { EditResourceDto, FetchError, GetAgendaItemResourcesDto, queryKeys, useAccessToken } from "../../..";
import { optimisticallyUpdateQueryData } from "../../utilities";
import { useSignalR } from "../../../../hooks";

type Variables = {
  agendaItemGuid: string;
  resourceGuid: string;
  title: string;
  url: string;
  isEmbedded: boolean;
  sortOrder: string;
};

const useEditAgendaItemResourceRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editAgendaItemResourceRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditResourceDto = {
      title: variables.title,
      url: variables.url,
      isEmbedded: variables.isEmbedded,
      sortOrder: variables.sortOrder,
      modifiedByUserId: "",
    };

    const response = await fetch(`${configuration.apiRootUrl}/agenda-item-resources/${variables.resourceGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editAgendaItemResourceRequest };
};

const useEditAgendaItemResource = () => {
  const { editAgendaItemResourceRequest } = useEditAgendaItemResourceRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editAgendaItemResourceRequest,
    onMutate: async (variables) => {
      let cachedResource = await optimisticallyUpdateQueryData<GetAgendaItemResourcesDto>(
        queryClient,
        queryKeys.meetings.getAgendaItemResources(variables.agendaItemGuid),
        (cachedData) => ({
          resources: cachedData.resources.map((resource) =>
            resource.guid === variables.resourceGuid
              ? {
                  ...resource,
                  title: variables.title,
                  url: variables.url,
                  isEmbedded: variables.isEmbedded,
                  sortOrder: variables.sortOrder,
                }
              : resource
          ),
        })
      );

      return { cachedResource };
    },
    onSuccess: async (_, variables) => {
      signalR.sendRefetchAgendaItemContent(variables.agendaItemGuid);
    },
    onError: async (_error, variables, context) => {
      queryClient.setQueryData(
        queryKeys.meetings.getAgendaItemResources(variables.agendaItemGuid),
        context?.cachedResource
      );
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.meetings.getAgendaItemResources(variables.agendaItemGuid),
      });
    },
  });
};

export { useEditAgendaItemResource };
